import { useState, useEffect, useRef, useCallback, type ChangeEvent } from 'react'
import { X, Search, ListChecks, Upload, Trash2 } from 'lucide-react'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import { designTokens } from '@/utils/design-tokens'
import { OrganizationService } from '@/api/generated/services/OrganizationService'
import { AcademyinfoService } from '@/api/generated/services/AcademyinfoService'
import type { OrgUpdateRequest } from '@/api/generated/models/OrgUpdateRequest'
import type { AcademyListItem } from '@/api/generated/models/AcademyListItem'
import type { OrgAcademyInfo } from '@/api/generated/models/OrgAcademyInfo'
import { useAlert } from '@/components/ui/Alert/exports'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { fetchWorkspaces } from '@/store/workspaceSlice'
import { resolveOrgLogoUrl } from '@/hooks/useOrgLogo'
import { handleApiError } from '@/utils/api-error-handler'
import HiddenIndicatorModal from './HiddenIndicatorModal'

interface OrganizationManagementModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

const LOGO_ACCEPT_TYPES = ['image/png', 'image/jpeg', 'image/webp']
const LOGO_MAX_BYTES = 1_000_000
// 탭 제목 기본값 (index.html 의 <title> 과 같다)
const DEFAULT_SITE_TITLE = '성과관리솔루션'
const SITE_TITLE_MAX = 100
// 타이틀 로고(브라우저 탭 아이콘)는 ICO 도 받는다. ICO 는 브라우저마다 type 이 달라 확장자로도 본다
const FAVICON_ACCEPT_TYPES = [...LOGO_ACCEPT_TYPES, 'image/x-icon', 'image/vnd.microsoft.icon']
const FAVICON_ACCEPT = [...FAVICON_ACCEPT_TYPES, '.ico'].join(',')

export default function OrganizationManagementModal({
  isOpen,
  onClose,
  onSuccess
}: OrganizationManagementModalProps) {
  const { showError, showSuccess } = useAlert()
  const dispatch = useAppDispatch()
  const { selectedWorkspace } = useAppSelector((state) => state.workspace)

  // Form state
  const [organizationName, setOrganizationName] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Academy (내 학교) state
  const [selectedAcademy, setSelectedAcademy] = useState<OrgAcademyInfo | null>(null)
  const [academySearchQuery, setAcademySearchQuery] = useState('')
  const [academySearchResults, setAcademySearchResults] = useState<AcademyListItem[]>([])
  const [isAcademySearching, setIsAcademySearching] = useState(false)
  const [isAcademyDropdownOpen, setIsAcademyDropdownOpen] = useState(false)
  const academyDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const academyDropdownRef = useRef<HTMLDivElement>(null)

  // 로고 state — 업로드·삭제는 저장 버튼과 별개로 바로 반영한다
  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [isLogoBusy, setIsLogoBusy] = useState(false)
  const logoInputRef = useRef<HTMLInputElement>(null)
  const [faviconUrl, setFaviconUrl] = useState<string | null>(null)
  const [isFaviconBusy, setIsFaviconBusy] = useState(false)
  const faviconInputRef = useRef<HTMLInputElement>(null)
  const [siteTitle, setSiteTitle] = useState('')

  // Form validation errors
  const [organizationNameError, setOrganizationNameError] = useState('')

  // Hidden indicator modal state
  const [isHiddenIndicatorModalOpen, setIsHiddenIndicatorModalOpen] = useState(false)

  // 공시년도 설정 state
  const [allYears, setAllYears] = useState<string[]>([])
  const [hiddenYears, setHiddenYears] = useState<string[]>([])
  const [defaultYear, setDefaultYear] = useState<string>('')
  const [resolvedDefaultYear, setResolvedDefaultYear] = useState<string>('')
  const [defaultYearError, setDefaultYearError] = useState('')

  // Fetch current organization data when modal opens
  useEffect(() => {
    if (isOpen && selectedWorkspace?.org_id) {
      fetchOrganizationData()
    }
  }, [isOpen, selectedWorkspace?.org_id])

  // Clear errors when user types
  useEffect(() => {
    setOrganizationNameError('')
  }, [organizationName])

  const fetchOrganizationData = async () => {
    if (!selectedWorkspace?.org_id) {
      showError('기관 정보를 찾을 수 없습니다.')
      return
    }

    setIsLoading(true)
    try {
      // org 정보와 전체 공시년도 목록을 병렬 로드
      // org_id 없이 호출 → 어드민 모드(hidden 미적용 전체 연도)
      const [response, yearsResponse] = await Promise.all([
        OrganizationService.appsOrganizationViewsGetOrg({
          orgId: selectedWorkspace.org_id,
        }),
        AcademyinfoService.appsAcademyinfoViewsGetAvailableYears({}),
      ])

      setOrganizationName(response.name || '')
      setLogoUrl(response.logo_url || null)
      setFaviconUrl(response.favicon_url || null)
      setSiteTitle(response.site_title || '')
      setSelectedAcademy(response.academy || null)
      setAllYears(yearsResponse.years || [])
      setHiddenYears(response.hidden_years || [])
      setDefaultYear(response.default_year || '')
      setResolvedDefaultYear(response.resolved_default_year || '')
    } catch (error: any) {
      console.error('Failed to fetch organization data:', error)

      if (error && typeof error === 'object' && 'status' in error) {
        switch (error.status) {
          case 401:
            showError('권한이 없습니다.')
            break
          case 404:
            showError('기관 정보를 찾을 수 없습니다.')
            break
          case 500:
            showError('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.')
            break
          default:
            showError('기관 정보를 불러오는 중 오류가 발생했습니다.')
        }
      } else {
        showError('기관 정보를 불러오는 중 오류가 발생했습니다.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Academy search
  const searchAcademies = useCallback(async (query: string) => {
    if (!query.trim()) {
      setAcademySearchResults([])
      return
    }
    setIsAcademySearching(true)
    try {
      const response = await AcademyinfoService.appsAcademyinfoViewsListAcademies({
        search: query,
        perPage: 10,
      })
      setAcademySearchResults(response.data || [])
    } catch (err) {
      console.error('Failed to search academies:', err)
    } finally {
      setIsAcademySearching(false)
    }
  }, [])

  const handleAcademySearchChange = (value: string) => {
    setAcademySearchQuery(value)
    setIsAcademyDropdownOpen(true)
    if (academyDebounceRef.current) clearTimeout(academyDebounceRef.current)
    academyDebounceRef.current = setTimeout(() => searchAcademies(value), 300)
  }

  const handleSelectAcademy = (academy: AcademyListItem) => {
    setSelectedAcademy(academy)
    setAcademySearchQuery('')
    setAcademySearchResults([])
    setIsAcademyDropdownOpen(false)
  }

  const handleClearAcademy = () => {
    setSelectedAcademy(null)
    setAcademySearchQuery('')
  }

  // 공시년도 hidden 토글
  const toggleHiddenYear = (year: string) => {
    setHiddenYears(prev => {
      const next = prev.includes(year)
        ? prev.filter(y => y !== year)
        : [...prev, year]
      // 숨김으로 전환되는 연도가 현재 default_year 라면 default 해제
      if (!prev.includes(year) && defaultYear === year) {
        setDefaultYear('')
      }
      return next
    })
  }

  const handleDefaultYearChange = (value: string) => {
    setDefaultYear(value)
    setDefaultYearError('')
  }

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (academyDropdownRef.current && !academyDropdownRef.current.contains(e.target as Node)) {
        setIsAcademyDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const validateForm = () => {
    let isValid = true

    // Reset errors
    setOrganizationNameError('')
    setDefaultYearError('')

    // Organization name validation
    if (!organizationName.trim()) {
      setOrganizationNameError('기관명을 입력해주세요.')
      isValid = false
    } else if (organizationName.trim().length < 2) {
      setOrganizationNameError('기관명은 2자 이상 입력해주세요.')
      isValid = false
    } else if (organizationName.trim().length > 100) {
      setOrganizationNameError('기관명은 100자 이하로 입력해주세요.')
      isValid = false
    }

    // default_year 가 hidden_years 에 포함되면 안됨
    if (defaultYear && hiddenYears.includes(defaultYear)) {
      setDefaultYearError('기본 공시년도는 숨김 연도로 설정할 수 없습니다.')
      isValid = false
    }

    return isValid
  }

  const handleSave = async () => {
    if (!validateForm()) {
      return
    }

    if (!selectedWorkspace?.org_id) {
      showError('기관 정보를 찾을 수 없습니다.')
      return
    }

    setIsSaving(true)

    try {
      const updateRequest: OrgUpdateRequest = {
        name: organizationName.trim(),
        academy_id: selectedAcademy?.id || '',
        hidden_years: hiddenYears,
        default_year: defaultYear,
        site_title: siteTitle.trim(),
      }

      const updated = await OrganizationService.appsOrganizationViewsUpdateOrg({
        orgId: selectedWorkspace.org_id,
        requestBody: updateRequest,
      })

      // 서버 응답으로 동기화 (resolved_default_year 갱신)
      setHiddenYears(updated.hidden_years || [])
      setDefaultYear(updated.default_year || '')
      setResolvedDefaultYear(updated.resolved_default_year || '')
      // 탭 제목이 바로 바뀌도록 워크스페이스 목록(선택 워크스페이스 포함)을 다시 받는다
      dispatch(fetchWorkspaces({}))

      showSuccess('기관 정보가 성공적으로 수정되었습니다.')
      onSuccess?.()
      handleClose()
    } catch (error: any) {
      console.error('Failed to update organization:', error)

      if (error && typeof error === 'object' && 'status' in error) {
        switch (error.status) {
          case 400:
            showError('입력 데이터가 올바르지 않습니다.')
            break
          case 401:
            showError('권한이 없습니다.')
            break
          case 404:
            showError('기관 정보를 찾을 수 없습니다.')
            break
          case 422: {
            const detail = error.body?.detail || error.body?.error?.message
            showError(typeof detail === 'string' ? detail : '입력값을 확인해주세요.')
            break
          }
          case 500:
            showError('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.')
            break
          default:
            showError('기관 정보 수정에 실패했습니다. 다시 시도해주세요.')
        }
      } else {
        showError('기관 정보 수정에 실패했습니다. 다시 시도해주세요.')
      }
    } finally {
      setIsSaving(false)
    }
  }

  // 사이드바 로고가 바로 바뀌도록 워크스페이스 목록(선택 워크스페이스 포함)을 다시 받는다
  const applyLogoChange = (nextLogoUrl: string | null | undefined) => {
    setLogoUrl(nextLogoUrl || null)
    dispatch(fetchWorkspaces({}))
  }

  const handleLogoFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = '' // 같은 파일을 다시 골라도 change 가 발생하도록
    if (!file || !selectedWorkspace?.org_id) return

    if (!LOGO_ACCEPT_TYPES.includes(file.type)) {
      showError('PNG, JPG, WEBP 이미지만 올릴 수 있습니다.')
      return
    }
    if (file.size > LOGO_MAX_BYTES) {
      showError('로고 이미지는 1MB 이하만 올릴 수 있습니다.')
      return
    }

    setIsLogoBusy(true)
    try {
      const updated = await OrganizationService.appsOrganizationViewsUploadOrgLogoEndpoint({
        orgId: selectedWorkspace.org_id,
        formData: { file },
      })
      applyLogoChange(updated.logo_url)
      showSuccess('로고를 바꿨습니다.')
    } catch (error) {
      handleApiError(error, showError, '로고 업로드에 실패했습니다')
    } finally {
      setIsLogoBusy(false)
    }
  }

  const handleLogoDelete = async () => {
    if (!selectedWorkspace?.org_id) return
    setIsLogoBusy(true)
    try {
      const updated = await OrganizationService.appsOrganizationViewsDeleteOrgLogoEndpoint({
        orgId: selectedWorkspace.org_id,
      })
      applyLogoChange(updated.logo_url)
      showSuccess('로고를 지우고 기본 DOT 로고로 되돌렸습니다.')
    } catch (error) {
      handleApiError(error, showError, '로고 삭제에 실패했습니다')
    } finally {
      setIsLogoBusy(false)
    }
  }

  // 탭 아이콘이 바로 바뀌도록 워크스페이스 목록(선택 워크스페이스 포함)을 다시 받는다
  const applyFaviconChange = (nextUrl: string | null | undefined) => {
    setFaviconUrl(nextUrl || null)
    dispatch(fetchWorkspaces({}))
  }

  const handleFaviconFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = '' // 같은 파일을 다시 골라도 change 가 발생하도록
    if (!file || !selectedWorkspace?.org_id) return

    if (!FAVICON_ACCEPT_TYPES.includes(file.type) && !file.name.toLowerCase().endsWith('.ico')) {
      showError('PNG, JPG, WEBP, ICO 이미지만 올릴 수 있습니다.')
      return
    }
    if (file.size > LOGO_MAX_BYTES) {
      showError('타이틀 로고 이미지는 1MB 이하만 올릴 수 있습니다.')
      return
    }

    setIsFaviconBusy(true)
    try {
      const updated = await OrganizationService.appsOrganizationViewsUploadOrgFaviconEndpoint({
        orgId: selectedWorkspace.org_id,
        formData: { file },
      })
      applyFaviconChange(updated.favicon_url)
      showSuccess('타이틀 로고를 바꿨습니다.')
    } catch (error) {
      handleApiError(error, showError, '타이틀 로고 업로드에 실패했습니다')
    } finally {
      setIsFaviconBusy(false)
    }
  }

  const handleFaviconDelete = async () => {
    if (!selectedWorkspace?.org_id) return
    setIsFaviconBusy(true)
    try {
      const updated = await OrganizationService.appsOrganizationViewsDeleteOrgFaviconEndpoint({
        orgId: selectedWorkspace.org_id,
      })
      applyFaviconChange(updated.favicon_url)
      showSuccess('타이틀 로고를 지우고 기본 아이콘으로 되돌렸습니다.')
    } catch (error) {
      handleApiError(error, showError, '타이틀 로고 삭제에 실패했습니다')
    } finally {
      setIsFaviconBusy(false)
    }
  }

  const handleClose = () => {
    setLogoUrl(null)
    setFaviconUrl(null)
    setSiteTitle('')
    setOrganizationName('')
    setOrganizationNameError('')
    setSelectedAcademy(null)
    setAcademySearchQuery('')
    setAcademySearchResults([])
    setIsAcademyDropdownOpen(false)
    setAllYears([])
    setHiddenYears([])
    setDefaultYear('')
    setResolvedDefaultYear('')
    setDefaultYearError('')
    onClose()
  }

  const isFormValid = organizationName.trim().length >= 2 &&
                     organizationName.trim().length <= 100 &&
                     !(defaultYear && hiddenYears.includes(defaultYear))

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      data-modal-overlay
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Background Blur Overlay */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}
        onClick={handleClose}
      />

      {/* Modal Content */}
      <div
        className="relative bg-white rounded w-[500px] max-w-[90vw] max-h-[90vh] overflow-auto border"
        style={{
          borderColor: designTokens.colors.gray[1]
        }}
      >
        <div className="flex flex-col gap-4 p-[30px]">
          {/* Header */}
          <div className="flex items-center justify-between gap-[10px] py-[7px]">
            <h2
              className="font-bold"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['title-medium'][0],
                lineHeight: designTokens.typography.fontSize['title-medium'][1].lineHeight,
                fontWeight: designTokens.typography.fontSize['title-medium'][1].fontWeight,
                color: designTokens.colors.gray[6]
              }}
            >
              기관 관리
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="w-6 h-6 flex items-center justify-center hover:opacity-70 transition-opacity"
              style={{ color: designTokens.colors.gray[5] }}
            >
              <X className="w-3 h-3" strokeWidth={2} />
            </button>
          </div>

          {/* Form Content */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-lg">기관 정보를 불러오는 중...</div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Organization Name Field */}
              <div className="space-y-2">
                <label
                  className="block font-normal"
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                    color: designTokens.colors.gray[6]
                  }}
                >
                  기관명 <span style={{ color: designTokens.colors.error }}>*</span>
                </label>
                <Input
                  type="text"
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  placeholder="기관명을 입력해주세요."
                  size="medium"
                  state={organizationNameError ? 'error' : 'default'}
                  disabled={isSaving}
                  className="w-full"
                />
                {organizationNameError && (
                  <p
                    className="text-sm"
                    style={{
                      color: designTokens.colors.error,
                      fontFamily: designTokens.typography.fontFamily.sans[0]
                    }}
                  >
                    {organizationNameError}
                  </p>
                )}
                <p
                  className="text-xs"
                  style={{
                    color: designTokens.colors.gray[4],
                    fontFamily: designTokens.typography.fontFamily.sans[0]
                  }}
                >
                  기관명은 2자 이상 100자 이하로 입력해주세요.
                </p>
              </div>

              {/* 로고 이미지 — 사이드바 상단에 표시. 없으면 기본 DOT 로고 */}
              <div className="space-y-2">
                <label
                  className="block font-normal"
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                    color: designTokens.colors.gray[6]
                  }}
                >
                  로고 이미지
                </label>
                <div
                  className="flex items-center justify-center rounded h-[72px] px-4"
                  style={{ backgroundColor: designTokens.colors.primary.dark }}
                  title="사이드바에 보이는 모습"
                >
                  {logoUrl ? (
                    <img
                      src={resolveOrgLogoUrl(logoUrl) || ''}
                      alt="기관 로고 미리보기"
                      className="max-w-[180px] max-h-[48px] object-contain"
                    />
                  ) : (
                    <div className="flex items-end gap-[7px]">
                      <img src="/images/logo-big.png" alt="기본 DOT 로고" className="w-[92px] h-[40px] object-contain" />
                      <span
                        style={{
                          color: designTokens.colors.gray[0],
                          fontSize: designTokens.typography.fontSize['logo-text'][0],
                          fontWeight: designTokens.typography.fontSize['logo-text'][1].fontWeight,
                          lineHeight: designTokens.typography.fontSize['logo-text'][1].lineHeight,
                          fontFamily: designTokens.typography.fontFamily.logo[0],
                        }}
                      >
                        성과관리
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept={LOGO_ACCEPT_TYPES.join(',')}
                    className="hidden"
                    onChange={handleLogoFileChange}
                  />
                  <Button
                    size="small"
                    color="light"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={isLogoBusy || isSaving}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    <Upload size={14} />
                    {isLogoBusy ? '처리 중...' : logoUrl ? '이미지 변경' : '이미지 업로드'}
                  </Button>
                  {logoUrl && (
                    <Button
                      size="small"
                      color="white"
                      onClick={handleLogoDelete}
                      disabled={isLogoBusy || isSaving}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      <Trash2 size={14} />
                      이미지 삭제
                    </Button>
                  )}
                </div>
                <p
                  className="text-xs"
                  style={{
                    color: designTokens.colors.gray[4],
                    fontFamily: designTokens.typography.fontFamily.sans[0]
                  }}
                >
                  PNG·JPG·WEBP, 1MB 이하. 올리면 바로 사이드바 로고가 바뀌고, 삭제하면 기본 DOT 로고로 돌아갑니다.
                  배경이 어두우므로 투명 배경·밝은 글씨 이미지를 권장합니다.
                </p>
              </div>

              {/* 타이틀 로고 — 브라우저 탭 아이콘(favicon). 없으면 기본 아이콘 */}
              <div className="space-y-2">
                <label
                  className="block font-normal"
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                    color: designTokens.colors.gray[6]
                  }}
                >
                  타이틀 로고
                </label>
                <div
                  className="flex items-center gap-2 rounded h-[44px] px-3 w-fit"
                  style={{ backgroundColor: designTokens.colors.gray[0], border: `1px solid ${designTokens.colors.gray[1]}` }}
                  title="브라우저 탭에 보이는 모습"
                >
                  <img
                    src={resolveOrgLogoUrl(faviconUrl) || '/favicon.png'}
                    alt="타이틀 로고 미리보기"
                    className="w-[20px] h-[20px] object-contain"
                  />
                  <span
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: designTokens.typography.fontSize['body-small'][0],
                      color: designTokens.colors.gray[6]
                    }}
                  >
                    {siteTitle.trim() || DEFAULT_SITE_TITLE}
                  </span>
                </div>
                <Input
                  type="text"
                  value={siteTitle}
                  onChange={(e) => setSiteTitle(e.target.value.slice(0, SITE_TITLE_MAX))}
                  placeholder={`탭 제목 (비워두면 '${DEFAULT_SITE_TITLE}')`}
                  size="medium"
                  disabled={isSaving}
                  className="w-full"
                />
                <div className="flex items-center gap-2">
                  <input
                    ref={faviconInputRef}
                    type="file"
                    accept={FAVICON_ACCEPT}
                    className="hidden"
                    onChange={handleFaviconFileChange}
                  />
                  <Button
                    size="small"
                    color="light"
                    onClick={() => faviconInputRef.current?.click()}
                    disabled={isFaviconBusy || isSaving}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    <Upload size={14} />
                    {isFaviconBusy ? '처리 중...' : faviconUrl ? '이미지 변경' : '이미지 업로드'}
                  </Button>
                  {faviconUrl && (
                    <Button
                      size="small"
                      color="white"
                      onClick={handleFaviconDelete}
                      disabled={isFaviconBusy || isSaving}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      <Trash2 size={14} />
                      이미지 삭제
                    </Button>
                  )}
                </div>
                <p
                  className="text-xs"
                  style={{
                    color: designTokens.colors.gray[4],
                    fontFamily: designTokens.typography.fontFamily.sans[0]
                  }}
                >
                  탭 제목은 아래 저장 버튼으로 반영되고, 비워두면 '{DEFAULT_SITE_TITLE}'로 표시됩니다.
                  아이콘은 PNG·JPG·WEBP·ICO, 1MB 이하이며 올리면 바로 바뀌고 삭제하면 기본 아이콘으로 돌아갑니다.
                  정사각형(예: 32×32, 64×64) 이미지를 권장합니다.
                </p>
              </div>

              {/* Academy (내 학교) Field */}
              <div className="space-y-2">
                <label
                  className="block font-normal"
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                    color: designTokens.colors.gray[6]
                  }}
                >
                  내 학교
                </label>

                {/* Selected academy display */}
                {selectedAcademy ? (
                  <div
                    className="flex items-center justify-between px-3 py-2 rounded border"
                    style={{ borderColor: designTokens.colors.gray[1], backgroundColor: designTokens.colors.gray[0] }}
                  >
                    <div>
                      <span
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '14px',
                          fontWeight: 600,
                          color: designTokens.colors.gray[6],
                        }}
                      >
                        {selectedAcademy.name}
                      </span>
                      {(selectedAcademy.establishment_type || selectedAcademy.region) && (
                        <span
                          style={{
                            fontFamily: designTokens.typography.fontFamily.sans[0],
                            fontSize: '12px',
                            color: designTokens.colors.gray[4],
                            marginLeft: '8px',
                          }}
                        >
                          {[selectedAcademy.establishment_type, selectedAcademy.region].filter(Boolean).join(' / ')}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleClearAcademy}
                      disabled={isSaving}
                      className="flex-shrink-0 hover:opacity-70 transition-opacity"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: designTokens.colors.gray[4] }}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  /* Search input */
                  <div className="relative" ref={academyDropdownRef}>
                    <div
                      className="flex items-center gap-2 px-3 py-2 rounded border"
                      style={{ borderColor: designTokens.colors.gray[2] }}
                    >
                      <Search className="w-4 h-4 flex-shrink-0" style={{ color: designTokens.colors.gray[4] }} />
                      <input
                        type="text"
                        value={academySearchQuery}
                        onChange={(e) => handleAcademySearchChange(e.target.value)}
                        onFocus={() => { if (academySearchQuery.trim()) setIsAcademyDropdownOpen(true) }}
                        placeholder="학교명으로 검색"
                        disabled={isSaving}
                        className="flex-1 outline-none bg-transparent"
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '14px',
                          color: designTokens.colors.gray[6],
                          border: 'none',
                        }}
                      />
                    </div>

                    {/* Dropdown results */}
                    {isAcademyDropdownOpen && (academySearchResults.length > 0 || isAcademySearching) && (
                      <div
                        className="absolute left-0 right-0 mt-1 bg-white rounded border shadow-lg z-10 max-h-[200px] overflow-auto"
                        style={{ borderColor: designTokens.colors.gray[2] }}
                      >
                        {isAcademySearching ? (
                          <div className="px-3 py-2 text-center" style={{ fontSize: '13px', color: designTokens.colors.gray[4] }}>
                            검색 중...
                          </div>
                        ) : (
                          academySearchResults.map(academy => (
                            <button
                              key={academy.id}
                              type="button"
                              onClick={() => handleSelectAcademy(academy)}
                              className="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors"
                              style={{
                                fontFamily: designTokens.typography.fontFamily.sans[0],
                                fontSize: '14px',
                                color: designTokens.colors.gray[6],
                                border: 'none',
                                background: 'none',
                                cursor: 'pointer',
                                borderBottom: `1px solid ${designTokens.colors.gray[0]}`,
                              }}
                            >
                              <span style={{ fontWeight: 500 }}>{academy.name}</span>
                              {(academy.establishment_type || academy.region) && (
                                <span style={{ fontSize: '12px', color: designTokens.colors.gray[4], marginLeft: '6px' }}>
                                  {[academy.establishment_type, academy.region].filter(Boolean).join(' / ')}
                                </span>
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}

                <p
                  className="text-xs"
                  style={{
                    color: designTokens.colors.gray[4],
                    fontFamily: designTokens.typography.fontFamily.sans[0]
                  }}
                >
                  대학알리미에서 사용할 내 학교를 설정합니다.
                </p>
              </div>

              {/* Hidden Indicator Management */}
              <div className="space-y-2">
                <label
                  className="block font-normal"
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                    color: designTokens.colors.gray[6]
                  }}
                >
                  공시항목 관리
                </label>
                <button
                  type="button"
                  onClick={() => setIsHiddenIndicatorModalOpen(true)}
                  disabled={isSaving || !selectedWorkspace?.org_id}
                  className="w-full flex items-center justify-between px-3 py-2 rounded border hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    borderColor: designTokens.colors.gray[2],
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                  }}
                >
                  <div className="flex items-center gap-2">
                    <ListChecks className="w-4 h-4" style={{ color: designTokens.colors.gray[5] }} />
                    <span
                      style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '14px',
                        color: designTokens.colors.gray[6],
                      }}
                    >
                      공시항목 표시/숨김 설정
                    </span>
                  </div>
                  <span
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '12px',
                      color: designTokens.colors.gray[4],
                    }}
                  >
                    설정하기 ›
                  </span>
                </button>
                <p
                  className="text-xs"
                  style={{
                    color: designTokens.colors.gray[4],
                    fontFamily: designTokens.typography.fontFamily.sans[0]
                  }}
                >
                  학교 비교 화면에 노출할 공시항목을 선택할 수 있습니다.
                </p>
              </div>

              {/* 공시년도 설정 */}
              <div className="space-y-2">
                <label
                  className="block font-normal"
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                    color: designTokens.colors.gray[6],
                  }}
                >
                  공시년도 숨김
                </label>
                {allYears.length === 0 ? (
                  <p
                    className="px-3 py-2 rounded border"
                    style={{
                      borderColor: designTokens.colors.gray[1],
                      backgroundColor: designTokens.colors.gray[0],
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '13px',
                      color: designTokens.colors.gray[4],
                    }}
                  >
                    수집된 공시년도가 없습니다.
                  </p>
                ) : (
                  <div
                    className="grid gap-2 px-3 py-3 rounded border"
                    style={{
                      gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                      borderColor: designTokens.colors.gray[1],
                      backgroundColor: designTokens.colors.gray[0],
                    }}
                  >
                    {allYears.map(year => {
                      const isHidden = hiddenYears.includes(year)
                      return (
                        <label
                          key={year}
                          className="flex items-center gap-2 cursor-pointer"
                          style={{ userSelect: 'none' }}
                        >
                          <input
                            type="checkbox"
                            checked={isHidden}
                            onChange={() => toggleHiddenYear(year)}
                            disabled={isSaving}
                            style={{
                              width: '14px',
                              height: '14px',
                              accentColor: designTokens.colors.primary.DEFAULT,
                              cursor: 'pointer',
                              flexShrink: 0,
                            }}
                          />
                          <span
                            style={{
                              fontFamily: designTokens.typography.fontFamily.sans[0],
                              fontSize: '13px',
                              color: isHidden ? designTokens.colors.gray[4] : designTokens.colors.gray[6],
                              textDecoration: isHidden ? 'line-through' : 'none',
                            }}
                          >
                            {year}
                          </span>
                        </label>
                      )
                    })}
                  </div>
                )}
                <p
                  className="text-xs"
                  style={{
                    color: designTokens.colors.gray[4],
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                  }}
                >
                  체크된 연도는 사용자 화면의 연도 셀렉트에서 제외됩니다.
                </p>
              </div>

              {/* 기본 공시년도 */}
              <div className="space-y-2">
                <label
                  className="block font-normal"
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                    color: designTokens.colors.gray[6],
                  }}
                >
                  기본 공시년도
                </label>
                <select
                  value={defaultYear}
                  onChange={e => handleDefaultYearChange(e.target.value)}
                  disabled={isSaving || allYears.length === 0}
                  className="w-full px-3 py-2 rounded border outline-none"
                  style={{
                    borderColor: defaultYearError ? designTokens.colors.error : designTokens.colors.gray[2],
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '14px',
                    color: designTokens.colors.gray[6],
                    backgroundColor: '#fff',
                  }}
                >
                  <option value="">
                    {resolvedDefaultYear ? `자동 — ${resolvedDefaultYear}` : '자동'}
                  </option>
                  {allYears
                    .filter(year => !hiddenYears.includes(year))
                    .map(year => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                </select>
                {defaultYearError && (
                  <p
                    className="text-sm"
                    style={{
                      color: designTokens.colors.error,
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                    }}
                  >
                    {defaultYearError}
                  </p>
                )}
                <p
                  className="text-xs"
                  style={{
                    color: designTokens.colors.gray[4],
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                  }}
                >
                  사용자 화면의 기본 선택 연도입니다. "자동" 선택 시 가장 최근의 표시 연도가 사용됩니다.
                </p>
              </div>
            </div>
          )}

          {/* Footer Buttons */}
          <div className="flex justify-end gap-3 pt-2">
            <Button
              size="medium"
              color="white"
              onClick={handleClose}
              disabled={isSaving}
            >
              취소
            </Button>
            <Button
              size="medium"
              color="dark"
              onClick={handleSave}
              disabled={isLoading || isSaving || !isFormValid}
            >
              {isSaving ? '저장 중...' : '저장'}
            </Button>
          </div>
        </div>
      </div>

      {/* Hidden Indicator Modal */}
      <HiddenIndicatorModal
        isOpen={isHiddenIndicatorModalOpen}
        orgId={selectedWorkspace?.org_id || ''}
        onClose={() => setIsHiddenIndicatorModalOpen(false)}
      />
    </div>
  )
}